import ElevenLabsWebSocket from './ElevenLabsWebSocket';

/**
 * SocketTTSStream — streaming TTS over the app's authenticated Socket.io
 * connection instead of a browser→ElevenLabs WebSocket (key-removal plan
 * Phase 2: docs/design/web-tts-key-removal-and-voice-unification.md §5.3).
 *
 * Subclasses ElevenLabsWebSocket and overrides ONLY the transport
 * (startStreaming/stop). All buffering, 2-chunk warm-up, progressive
 * playback, blob assembly and callback semantics are inherited unchanged, so
 * consumers (TextToSpeech, ConversationAutoplay) behave identically.
 *
 * Server events (mirrors server/types/socketEvents.js TTS_STREAM):
 *   emit 'client:tts:stream_start'  { requestId, text, voiceId, role, interface }
 *   emit 'client:tts:stream_cancel' { requestId }
 *   on   'server:tts:chunk'         { requestId, seq, audio|null, isFinal }
 *   on   'server:tts:error'         { requestId, code, message, fallback }
 */
class SocketTTSStream extends ElevenLabsWebSocket {
  constructor(
    socket,
    voiceId,
    // Parity with the parent signature from here on:
    model = 'eleven_turbo_v2_5',
    useProgressivePlayback = true,
    disableInternalPlayback = false,
    { role = 'ai_therapist', interface: interfaceName = 'client_chat' } = {}
  ) {
    // No API key in the browser — that's the point.
    super(null, voiceId, model, useProgressivePlayback, disableInternalPlayback);
    this.socket = socket;
    this.role = role;
    this.interfaceName = interfaceName;
    this.requestId = null;
    this._chunkHandler = null;
    this._errorHandler = null;
  }

  _detachSocketHandlers() {
    if (this._chunkHandler) this.socket?.off('server:tts:chunk', this._chunkHandler);
    if (this._errorHandler) this.socket?.off('server:tts:error', this._errorHandler);
    this._chunkHandler = null;
    this._errorHandler = null;
  }

  // Transport override: same state machine as the parent's onmessage handler,
  // fed by relay events instead of a direct ElevenLabs WebSocket.
  async startStreaming(text) {
    try {
      if (!this.socket || !this.socket.connected) {
        throw new Error('Socket not connected — cannot stream TTS via relay');
      }
      if (this.callbacks.onStart) this.callbacks.onStart();

      // Reset state (mirrors parent)
      this.audioBuffer = [];
      this.accumulatedChunks = 0;
      this.playedChunks = 0;
      this.lastPlayedChunkIndex = -1;
      this.isPlaying = false;
      this.isFirstPlay = true;
      this.isUpdating = false;
      this.allChunksReceived = false;
      this.totalAudioDuration = 0;

      this.requestId = `tts-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const requestId = this.requestId;

      this._chunkHandler = async (data) => {
        if (data?.requestId !== requestId) return;
        try {
          if (data.audio) {
            if (this.isValidBase64(data.audio)) {
              this.audioBuffer.push(data.audio);
              this.accumulatedChunks++;

              if (this.callbacks.onAudioChunk) {
                this.callbacks.onAudioChunk(data.audio, this.accumulatedChunks);
              }

              if (!this.disableInternalPlayback) {
                if (this.isFirstPlay) {
                  if (this.useProgressivePlayback) {
                    if (this.accumulatedChunks >= this.MIN_CHUNKS_TO_PLAY) {
                      this.isFirstPlay = false;
                      this.playCompleteAudio();
                    }
                  } else if (this.accumulatedChunks === 1) {
                    this.isFirstPlay = false;
                    this.waitAndPlayCompleteAudio();
                  }
                } else if (this.useProgressivePlayback && !this.isPlaying &&
                           this.lastPlayedChunkIndex < this.audioBuffer.length - 1) {
                  this.playCompleteAudio();
                }
              }

              // Generation-only early-blob notification (parity with parent)
              if (this.disableInternalPlayback &&
                  this.accumulatedChunks >= this.MIN_CHUNKS_TO_PLAY &&
                  this.isFirstPlay) {
                this.isFirstPlay = false;
                if (this.callbacks.onChunksReady) {
                  const earlyBlob = this.createAudioBlob(0, this.audioBuffer.length);
                  if (earlyBlob) {
                    this.callbacks.onChunksReady(URL.createObjectURL(earlyBlob));
                  }
                }
              }
            }
          }

          if (data.isFinal) {
            this.allChunksReceived = true;
            this.isStreamComplete = true;
            this._detachSocketHandlers();

            if (!this.disableInternalPlayback) {
              if (this.isFirstPlay && this.audioBuffer.length > 0) {
                this.isFirstPlay = false;
                await this.playCompleteAudio();
              } else if (this.audioBuffer.length > 0 && !this.isPlaying &&
                         this.lastPlayedChunkIndex < this.audioBuffer.length - 1) {
                this.playCompleteAudio();
              }
            } else if (this.callbacks.onComplete) {
              // Generation-only mode: signal completion like the parent's onclose.
              this.callbacks.onComplete();
            }
          }
        } catch (error) {
          console.error('SocketTTSStream: error processing chunk:', error);
          if (this.callbacks.onError) this.callbacks.onError(error);
        }
      };

      this._errorHandler = (data) => {
        if (data?.requestId !== requestId) return;
        console.error('SocketTTSStream: relay error:', data.code, data.message);
        this._detachSocketHandlers();
        if (this.callbacks.onError) {
          const err = new Error(data.message || 'TTS relay error');
          err.code = data.code;
          err.fallback = data.fallback; // 'rest' — caller may retry buffered
          this.callbacks.onError(err);
        }
      };

      this.socket.on('server:tts:chunk', this._chunkHandler);
      this.socket.on('server:tts:error', this._errorHandler);

      this.socket.emit('client:tts:stream_start', {
        requestId,
        text,
        voiceId: this.voiceId,
        role: this.role,
        interface: this.interfaceName,
      });
    } catch (error) {
      console.error('SocketTTSStream: error starting stream:', error);
      if (this.callbacks.onError) this.callbacks.onError(error);
    }
  }

  stop() {
    if (this.requestId && this.socket?.connected) {
      this.socket.emit('client:tts:stream_cancel', { requestId: this.requestId });
    }
    this._detachSocketHandlers();
    this.requestId = null;
    // Parent stop() handles audio teardown + state reset; its WebSocket close
    // is a no-op here (this.websocket is never set on the relay path).
    super.stop();
  }
}

export default SocketTTSStream;
